import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import BoardIcon from '@atlaskit/icon/core/board';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import GridIcon from '@atlaskit/icon/core/grid';
import LinkBrokenIcon from '@atlaskit/icon/core/link-broken';
import TableIcon from '@atlaskit/icon/core/table';
import { messages } from '@databases/api-views/messages';
import { messages as globalMessages } from '@databases/intl/messages';
import { type DefaultRootState } from '@databases/redux';
import { type DistributiveOmit } from '@databases/types';
import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  isDatabaseTextValue,
} from '@databases/types/database';
import {
  type DatabaseFilter,
  type DatabaseView,
  DatabaseFilterType,
  DatabaseLayoutType,
  isFilterWithUsageType,
} from '@databases/types/database/view';
import { getStringRepresentation } from '@databases/utils/database/field-types';
import {
  isValidDateString,
  toDisplayDate,
} from '@databases/utils/database/field-types/date';
import {
  getJiraColumnTypeFromState,
  getJiraFieldEquivalentType,
} from '@databases/utils/database/field-types/jiraIssueDetails';
import { getNumberDisplayString } from '@databases/utils/database/field-types/number';
import {
  createEntry,
  createField,
  getDatabaseFieldOptional,
} from '@databases/utils/database/utils';
import { type ViewChangeType } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type ReactElement } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import LayoutIcon from '../components/layoutIcon/LayoutIcon';

type GetFormattedFilterValueOptions = {
  filter: DatabaseFilter;
  state: DefaultRootState;
  locale: string;
  contentId: string;
};

export type TypedEmptyFilterObject = DistributiveOmit<
  DatabaseFilter,
  'value' | 'fieldId'
>;

export type UpdateFilterType = (
  filter: DatabaseFilter,
  newFilter: TypedEmptyFilterObject
) => DatabaseFilter;

export const getLayoutOptionTitle = (layoutType: DatabaseLayoutType) => {
  switch (layoutType) {
    case DatabaseLayoutType.TABLE:
      return messages['layout.table-text'];
    case DatabaseLayoutType.GALLERY:
      return messages['layout.gallery-text'];
    case DatabaseLayoutType.BOARD:
      return messages['layout.board-text'];
    case DatabaseLayoutType.CALENDAR:
      return messages['layout.calendar-text'];
  }
};

type GetLayoutIconProps = {
  type: DatabaseLayoutType;
  iconSize: 'small' | 'xlarge' | 'large' | 'medium';
  isDefault?: boolean;
  isPresentationOnly?: boolean;
  isUnsavedView?: boolean;
};

export const getLayoutIcons = ({
  type,
  iconSize,
  isDefault = false,
  isPresentationOnly = true,
  isUnsavedView = false,
}: GetLayoutIconProps): {
  label: { id: string; defaultMessage: string; description: string };
  icon: ReactElement;
} => {
  if (isUnsavedView) {
    return {
      label: globalMessages['view.unsaved-view'],
      icon: (
        <LayoutIcon
          IconComponent={(iconProps) => (
            <LinkBrokenIcon {...iconProps} size="medium" testId="unsaved-view-icon" />
          )}
          iconSize={iconSize}
          layoutType={globalMessages['view.unsaved-view']}
          isDefault={isDefault}
          isPresentationOnly={true}
        />
      ),
    };
  }

  const layoutOptionTitle = getLayoutOptionTitle(type);

  switch (type) {
    case DatabaseLayoutType.TABLE:
      return {
        label: layoutOptionTitle,
        icon: (
          <LayoutIcon
            IconComponent={(iconProps) => <TableIcon {...iconProps} size="medium" />}
            iconSize={iconSize}
            layoutType={layoutOptionTitle}
            isDefault={isDefault}
            isPresentationOnly={isPresentationOnly}
          />
        ),
      };
    case DatabaseLayoutType.GALLERY:
      return {
        label: layoutOptionTitle,
        icon: (
          <LayoutIcon
            IconComponent={(iconProps) => <GridIcon {...iconProps} size="medium" />}
            iconSize={iconSize}
            layoutType={layoutOptionTitle}
            isDefault={isDefault}
            isPresentationOnly={isPresentationOnly}
          />
        ),
      };
    case DatabaseLayoutType.BOARD:
      return {
        label: layoutOptionTitle,
        icon: (
          <LayoutIcon
            IconComponent={(iconProps) => <BoardIcon {...iconProps} size="medium" />}
            iconSize={iconSize}
            layoutType={layoutOptionTitle}
            isDefault={isDefault}
            isPresentationOnly={isPresentationOnly}
          />
        ),
      };
    case DatabaseLayoutType.CALENDAR:
      return {
        label: layoutOptionTitle,
        icon: (
          <LayoutIcon
            IconComponent={(iconProps) => <CalendarIcon {...iconProps} size="medium" />}
            iconSize={iconSize}
            layoutType={layoutOptionTitle}
            isDefault={isDefault}
            isPresentationOnly={isPresentationOnly}
          />
        ),
      };
  }
};

export const getSortIcon = (ascending: boolean) => {
  return ascending ? (
    <ArrowUpIcon label="" testId="sort-icon-ascending" />
  ) : (
    <ArrowDownIcon label="" testId="sort-icon-descending" />
  );
};

export const getFormattedFilterValue = ({
  filter,
  state,
  locale,
  contentId,
}: GetFormattedFilterValueOptions) => {
  const fieldId = isFilterWithUsageType(filter) ? undefined : filter.fieldId;
  const database = selectDatabaseByContentIdRequired(state, {
    contentId,
  });
  const field = getDatabaseFieldOptional(database, fieldId);
  if (
    !field ||
    isFilterWithUsageType(filter) ||
    filter.type === DatabaseFilterType.EMPTY
  ) {
    return undefined;
  }

  if (filter.type === DatabaseFilterType.CONTAINS) {
    return filter.value;
  }

  if (field.type === DatabaseFieldType.JIRA_ISSUE_DETAILS) {
    const jiraColumnType = getJiraColumnTypeFromState(
      field as DatabaseField<DatabaseFieldType.JIRA_ISSUE_DETAILS>,
      state.jira?.issues
    );
    const equivalentType = getJiraFieldEquivalentType(jiraColumnType);
    const { value } = filter.value;

    if (Array.isArray(value) && value.length > 0) {
      return value.join(', ');
    }

    const plainValue = isDatabaseTextValue(value)
      ? value.plain
      : typeof value === 'string'
      ? value
      : undefined;

    if (plainValue && plainValue !== '') {
      if (equivalentType === DatabaseFieldType.DATE && isValidDateString(plainValue)) {
        return toDisplayDate(plainValue, locale);
      }
      if (equivalentType === DatabaseFieldType.NUMBER) {
        return getNumberDisplayString(plainValue, field, locale);
      }
      return plainValue;
    }
    return undefined;
  }

  if (filter.value.type === DatabaseFieldType.SELECT) {
    const tags = getStringRepresentation({ database, field, value: filter.value }, state);
    return tags?.split('\n').join(', ');
  }

  if (
    filter.value.type === DatabaseFieldType.DATE &&
    filter.value.value &&
    isValidDateString(filter.value.value)
  ) {
    return toDisplayDate(filter.value.value, locale);
  }

  if (filter.value.type === DatabaseFieldType.NUMBER) {
    return filter.value.value === ''
      ? ''
      : getNumberDisplayString(filter.value.value, field, locale);
  }

  return getStringRepresentation({ database, field, value: filter.value }, state);
};

export function updateFilterType(
  filter: DatabaseFilter,
  newFilter: TypedEmptyFilterObject,
  contentId: string,
  state: DefaultRootState
): DatabaseFilter {
  const oldValue: DatabaseValue =
    filter.type === DatabaseFilterType.EQUALS
      ? filter.value
      : filter.type === DatabaseFilterType.GREATER
      ? filter.value
      : filter.type === DatabaseFilterType.LESS
      ? filter.value
      : filter.type === DatabaseFilterType.CONTAINS
      ? { type: DatabaseFieldType.TEXT, value: { plain: filter.value } }
      : { type: DatabaseFieldType.TEXT, value: { plain: '' } };

  const database = selectDatabaseByContentIdRequired(state, {
    contentId,
  });
  const field =
    getDatabaseFieldOptional(
      database,
      'fieldId' in filter ? filter.fieldId : undefined
    ) ?? createField(DatabaseFieldType.TEXT);
  const entry = createEntry(database);

  const newValue =
    newFilter.type === DatabaseFilterType.EQUALS
      ? oldValue
      : newFilter.type === DatabaseFilterType.GREATER
      ? oldValue
      : newFilter.type === DatabaseFilterType.LESS
      ? oldValue
      : newFilter.type === DatabaseFilterType.CONTAINS
      ? getStringRepresentation({ database, field, entry, value: oldValue }, state)
      : undefined;

  return {
    ...newFilter,
    fieldId: 'fieldId' in filter ? filter.fieldId : undefined,
    value: newValue,
  } as DatabaseFilter;
}

export const createLiveMockAdapter = (
  contentId: string,
  currentView: DatabaseView,
  setViewChange: (delta: ViewChangeType | ViewChangeType[]) => void,
  fields: DatabaseField[] = [
    {
      id: 'text-field-1',
      name: 'Field 1',
      type: DatabaseFieldType.TEXT,
      settings: {},
    } as DatabaseField,
    {
      id: 'text-field-2',
      name: 'Blah',
      type: DatabaseFieldType.TEXT,
      settings: {},
    } as DatabaseField,
    {
      id: 'select-field',
      name: 'Tag field',
      type: DatabaseFieldType.SELECT,
      settings: {
        multi: false,
      },
    } as DatabaseField,
    {
      id: 'attachment-field',
      name: 'Attachment field',
      type: DatabaseFieldType.ATTACHMENT,
      settings: {},
    } as DatabaseField,
  ],
  state: DefaultRootState
) => {
  return {
    contentId,
    getFieldById: (fieldId: string) =>
      fields.find((field) => field.id === fieldId) as DatabaseField | undefined,
    currentView,
    setViewChange,
    fields,
    updateFilterType: (filter: DatabaseFilter, newFilter: TypedEmptyFilterObject) =>
      updateFilterType(filter, newFilter, contentId, state),
    getFormattedFilterValue: (filter: DatabaseFilter) =>
      getFormattedFilterValue({
        filter,
        state,
        locale: 'en-US',
        contentId,
      }),
    getExperimentValue,
  };
};
